import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { formatMoney as formatShopifyMoney } from '@theme/money-formatting';

class WholesaleProductForm extends HTMLElement {
  isSubmitting = false;
  cartCount = 0;

  connectedCallback() {
    this.cartCount = Number(this.dataset.cartCount) || 0;
    this.form = this.querySelector('[data-wholesale-form]');
    this.submitButton = this.querySelector('[data-submit-button]');
    this.checkoutButton = this.querySelector('[data-checkout-button]');
    this.submitLabel = this.querySelector('[data-submit-label]');
    this.errorMessage = this.querySelector('[data-error-message]');
    this.liveRegion = this.querySelector('[data-live-region]');
    this.inputs = [...this.querySelectorAll('[data-quantity-input]')];
    this.quantityControls = [...this.querySelectorAll('[data-quantity-action], [data-quantity-input]')];
    this.quantityControls.forEach((control) => {
      control.dataset.initiallyDisabled = control.disabled ? 'true' : 'false';
    });

    this.addEventListener('click', this.handleQuantityClick);
    this.addEventListener('input', this.handleQuantityInput);
    this.addEventListener('change', this.handleQuantityChange);
    this.form?.addEventListener('submit', this.handleSubmit);
    this.checkoutButton?.addEventListener('click', this.handleCheckout);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.handleExternalCartUpdate);

    this.updateSummary();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleQuantityClick);
    this.removeEventListener('input', this.handleQuantityInput);
    this.removeEventListener('change', this.handleQuantityChange);
    this.form?.removeEventListener('submit', this.handleSubmit);
    this.checkoutButton?.removeEventListener('click', this.handleCheckout);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.handleExternalCartUpdate);
  }

  handleQuantityClick = (event) => {
    const button = event.target.closest('[data-quantity-action]');
    if (!button || button.disabled) return;

    const row = button.closest('[data-variant-row]');
    const input = row?.querySelector('[data-quantity-input]');
    if (!input) return;

    const current = this.readQuantity(input);
    const direction = button.dataset.quantityAction;
    const next = direction === 'plus' ? this.nextQuantity(input, current) : this.previousQuantity(input, current);
    input.value = String(next);
    this.hideError();
    this.updateSummary();
  };

  handleQuantityInput = (event) => {
    if (!event.target.matches('[data-quantity-input]')) return;
    this.hideError();
    this.updateSummary();
  };

  handleQuantityChange = (event) => {
    if (!event.target.matches('[data-quantity-input]')) return;
    event.target.value = String(this.normalizeQuantity(event.target, this.readQuantity(event.target)));
    this.updateSummary();
  };

  readQuantity(input) {
    const quantity = Number.parseInt(input.value, 10);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 0;
  }

  nextQuantity(input, current) {
    const minimum = Number(input.dataset.orderMin) || 1;
    const increment = Number(input.dataset.orderIncrement) || 1;
    const cartQuantity = Number(input.dataset.cartQuantity) || 0;
    const firstAddition = cartQuantity > 0 ? increment : minimum;
    return this.normalizeQuantity(input, current === 0 ? firstAddition : current + increment);
  }

  previousQuantity(input, current) {
    const minimum = Number(input.dataset.orderMin) || 1;
    const increment = Number(input.dataset.orderIncrement) || 1;
    const cartQuantity = Number(input.dataset.cartQuantity) || 0;
    const firstAddition = cartQuantity > 0 ? increment : minimum;
    if (current <= firstAddition) return 0;
    return this.normalizeQuantity(input, current - increment);
  }

  normalizeQuantity(input, quantity) {
    if (!Number.isFinite(quantity) || quantity <= 0) return 0;

    const minimum = Number(input.dataset.orderMin) || 1;
    const increment = Number(input.dataset.orderIncrement) || 1;
    const cartQuantity = Number(input.dataset.cartQuantity) || 0;
    const firstAddition = cartQuantity > 0 ? increment : minimum;
    const maximum = input.max === '' ? Number.POSITIVE_INFINITY : Number(input.max);
    const stepped = quantity <= firstAddition
      ? firstAddition
      : firstAddition + Math.round((quantity - firstAddition) / increment) * increment;
    if (maximum < firstAddition) return 0;
    const alignedMaximum = Number.isFinite(maximum)
      ? firstAddition + Math.floor((maximum - firstAddition) / increment) * increment
      : maximum;
    return Math.max(0, Math.min(stepped, alignedMaximum));
  }

  getSelectedItems({ normalize = false } = {}) {
    return this.inputs.flatMap((input) => {
      if (input.disabled) return [];
      const quantity = normalize
        ? this.normalizeQuantity(input, this.readQuantity(input))
        : this.readQuantity(input);
      if (normalize) input.value = String(quantity);
      if (quantity === 0) return [];

      const row = input.closest('[data-variant-row]');
      return [{
        id: Number(row.dataset.variantId),
        quantity,
        price: Number(row.dataset.price) || 0,
      }];
    });
  }

  updateSummary() {
    const items = this.getSelectedItems();
    const selectedCount = items.reduce((total, item) => total + item.quantity, 0);
    const subtotal = items.reduce((total, item) => total + item.quantity * item.price, 0);

    const countElement = this.querySelector('[data-selected-count]');
    const subtotalElement = this.querySelector('[data-selected-subtotal]');
    if (countElement) countElement.textContent = String(selectedCount);
    if (subtotalElement) subtotalElement.textContent = this.formatMoney(subtotal);
    if (this.submitButton) this.submitButton.disabled = selectedCount === 0 || this.isSubmitting;

    this.updateMoq(selectedCount);
  }

  updateMoq(stagedCount = 0) {
    const minimum = Number(this.dataset.minimumOrder) || 10;
    const previewCount = this.cartCount + stagedCount;
    const displayCount = Math.min(previewCount, minimum);
    const remaining = Math.max(minimum - previewCount, 0);
    const progress = Math.min((previewCount / minimum) * 100, 100);
    const progressElement = this.querySelector('[data-moq-progress]');
    const countElement = this.querySelector('[data-moq-count]');
    const barElement = this.querySelector('[data-moq-bar]');
    const messageElement = this.querySelector('[data-moq-message]');

    if (progressElement) progressElement.setAttribute('aria-valuenow', String(displayCount));
    if (countElement) countElement.textContent = `${previewCount} / ${minimum}`;
    if (barElement) barElement.style.width = `${progress}%`;
    if (messageElement) {
      messageElement.textContent = remaining > 0
        ? `Add ${remaining} more ${remaining === 1 ? 'piece' : 'pieces'} to reach the ${minimum}-piece order minimum.`
        : 'Minimum reached. You can add more styles and sizes.';
    }
  }

  formatMoney(cents) {
    const currency = this.dataset.currency || 'NGN';
    const moneyFormat = this.dataset.moneyFormat || '{{amount}} {{currency}}';
    return formatShopifyMoney(cents, moneyFormat, currency);
  }

  getCartSectionIds() {
    return [...document.querySelectorAll('cart-items-component')]
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);
  }

  setSubmitting(isSubmitting) {
    this.isSubmitting = isSubmitting;
    this.toggleAttribute('data-loading', isSubmitting);
    this.quantityControls?.forEach((control) => {
      control.disabled = isSubmitting || control.dataset.initiallyDisabled === 'true';
    });
    if (this.submitLabel) {
      this.submitLabel.textContent = isSubmitting ? this.dataset.addingLabel : this.dataset.addLabel;
    }
    if (this.checkoutButton) this.checkoutButton.disabled = isSubmitting;
    this.updateSummary();
  }

  handleSubmit = async (event) => {
    event.preventDefault();
    if (this.isSubmitting) return;

    const items = this.getSelectedItems({ normalize: true });
    if (items.length === 0) {
      this.showError('Choose at least one available size before adding to cart.');
      return;
    }

    await this.addSelectedItems(items);
  };

  handleCheckout = async () => {
    if (this.isSubmitting) return;

    const items = this.getSelectedItems({ normalize: true });
    if (items.length === 0 || await this.addSelectedItems(items)) {
      this.navigateToCart();
    }
  };

  navigateToCart() {
    window.location.assign(this.dataset.cartUrl || Theme.routes.cart_url);
  }

  async addSelectedItems(items) {
    if (this.isSubmitting || items.length === 0) return false;

    this.setSubmitting(true);
    this.hideError();

    const totalQuantity = items.reduce((total, item) => total + item.quantity, 0);
    const deferredPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferredPromise.promise,
    }));

    try {
      const sectionIds = this.getCartSectionIds();
      const payload = {
        items: items.map(({ id, quantity }) => ({ id, quantity })),
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      };
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'We could not add those sizes. Please check the quantities and try again.');
      }

      const ajaxCart = await this.fetchCart();
      this.cartCount = Number(ajaxCart.item_count) || 0;
      this.syncVariantRules(ajaxCart.items);
      deferredPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          sections: data.sections,
          didError: false,
          itemCount: totalQuantity,
          productId: this.dataset.productId,
          source: 'wholesale-product-form',
          sourceId: this.dataset.productId,
        },
      });

      this.inputs.forEach((input) => { input.value = '0'; });
      this.liveRegion.textContent = this.dataset.addedLabel || 'Selected sizes added to your cart.';
      return true;
    } catch (error) {
      const message = error?.message || 'A network error occurred. Please try again.';
      this.showError(message);
      this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));

      try {
        const ajaxCart = await this.fetchCart();
        this.cartCount = Number(ajaxCart.item_count) || this.cartCount;
        this.syncVariantRules(ajaxCart.items);
        deferredPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: { didError: true, items: ajaxCart.items, itemCount: totalQuantity, source: 'wholesale-product-form' },
        });
      } catch (cartError) {
        deferredPromise.reject(cartError);
      }
      return false;
    } finally {
      this.setSubmitting(false);
    }
  }

  handleExternalCartUpdate = (event) => {
    if (!event.promise) return;
    event.promise.then(async ({ cart, detail }) => {
      if (Number.isFinite(cart?.totalQuantity)) {
        this.cartCount = cart.totalQuantity;
      }
      if (detail?.items) {
        this.syncVariantRules(detail.items);
      } else {
        const ajaxCart = await this.fetchCart();
        this.cartCount = Number(ajaxCart.item_count) || 0;
        this.syncVariantRules(ajaxCart.items);
      }
      this.updateSummary();
    }).catch(() => {});
  };

  syncVariantRules(items = []) {
    const quantities = new Map();
    for (const item of items) {
      const variantId = String(item.variant_id ?? item.id ?? '');
      if (!variantId) continue;
      quantities.set(variantId, (quantities.get(variantId) || 0) + (Number(item.quantity) || 0));
    }

    for (const input of this.inputs) {
      const row = input.closest('[data-variant-row]');
      if (!row) continue;

      const cartQuantity = quantities.get(String(row.dataset.variantId)) || 0;
      const minimum = Number(input.dataset.orderMin) || 1;
      const increment = Number(input.dataset.orderIncrement) || 1;
      const firstAddition = cartQuantity > 0 ? increment : minimum;
      const absoluteMax = input.dataset.absoluteMax === ''
        ? Number.POSITIVE_INFINITY
        : Number(input.dataset.absoluteMax);
      const remainingMax = Number.isFinite(absoluteMax) ? Math.max(absoluteMax - cartQuantity, 0) : absoluteMax;
      const baseAvailable = row.dataset.variantAvailable === 'true';
      const canOrder = baseAvailable && remainingMax >= firstAddition;

      input.dataset.cartQuantity = String(cartQuantity);
      if (Number.isFinite(remainingMax)) input.max = String(remainingMax);
      else input.removeAttribute('max');

      const controls = [...row.querySelectorAll('[data-quantity-action], [data-quantity-input]')];
      for (const control of controls) {
        control.dataset.initiallyDisabled = canOrder ? 'false' : 'true';
        control.disabled = this.isSubmitting || !canOrder;
      }

      row.classList.toggle('is-sold-out', !canOrder);
      const soldOutLabel = row.querySelector('[data-sold-out-label]');
      if (soldOutLabel) soldOutLabel.hidden = canOrder;
      input.value = String(this.normalizeQuantity(input, this.readQuantity(input)));
    }
  }

  async fetchCart() {
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      if (typeof cartItemsComponent.fetchCartData === 'function') return cartItemsComponent.fetchCartData();
    }

    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error('Unable to refresh the cart.');
    return response.json();
  }

  showError(message) {
    if (this.errorMessage) {
      this.errorMessage.textContent = message;
      this.errorMessage.hidden = false;
    }
    if (this.liveRegion) this.liveRegion.textContent = message;
  }

  hideError() {
    if (this.errorMessage) {
      this.errorMessage.textContent = '';
      this.errorMessage.hidden = true;
    }
  }
}

if (!customElements.get('wholesale-product-form')) {
  customElements.define('wholesale-product-form', WholesaleProductForm);
}
